import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { useStoreWhatsApp, buildWhatsAppUrl } from '@/hooks/useStoreWhatsApp';
import { htmlToText } from '@/lib/display';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';
import { getOrganizationByCommerce } from '@/lib/zifekCore';
import ReviewsSection from '@/components/feature/ReviewsSection';
import RecommendedSection from '@/components/feature/RecommendedSection';
import FavoriteButton from '@/components/feature/FavoriteButton';

interface ServiceItem {
  id: number;
  titre: string;
  description: string;
  detailssup: string | null;
  product_image: string;
  prix: number;
  prix_promo: number | null;
  tags: string | null;
  pays: string | null;
  ville: string | null;
  owner: number | null;
  idcommerce: number | null;
  form_id?: number | null;
  categorie_id?: number | null;
  created_at: string;
}

export default function DetailServicePage() {
  const { id } = useParams<{ id: string }>();
  const { tenant, isTenant, loading: tenantLoading } = useTenant();
  const whatsapp = useStoreWhatsApp();

  const [service, setService] = useState<ServiceItem | null>(null);
  const [bookingServiceId, setBookingServiceId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeImage, setActiveImage] = useState(0);
  const [organizationId, setOrganizationId] = useState<number | null>(null);
  const [categoryChain, setCategoryChain] = useState<{ id: number; nom: string }[]>([]);

  const monaie = tenant?.monaie || 'MAD';
  // Prix affichés dans la devise choisie par le visiteur (taux du jour).
  const { display, original } = useDisplayCurrency(monaie);

  useEffect(() => {
    if (!id) {
      setError('Service introuvable');
      setLoading(false);
      return;
    }
    // Attente de la résolution du tenant : évite d'afficher un service
    // avant de connaître le commerce courant (isolation par commerce).
    if (tenantLoading) return;
    setLoading(true);
    let query = supabase
      .from('nospartenairesservices')
      .select('*')
      .eq('id', Number(id));
    // Sur la boutique d'un commerce, on n'accepte QUE ses propres services.
    if (isTenant && tenant) query = query.eq('idcommerce', tenant.id);
    query
      .maybeSingle()
      .then(({ data, error: fetchError }) => {
        if (fetchError) setError(fetchError.message);
        else if (!data) setError('Service introuvable');
        else setService(data as ServiceItem);
      })
      .catch(() => setError('Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [id, isTenant, tenant, tenantLoading]);

  useEffect(() => {
    if (service?.titre) document.title = service.titre;
  }, [service]);

  // Chaîne de catégories complète du service : Catégorie › Sous-catégorie › Sous-sous.
  // On remonte la hiérarchie via `parent_id` (table categorieservices).
  useEffect(() => {
    const leafId = Number(service?.categorie_id || 0);
    if (!leafId) {
      setCategoryChain([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const chain: { id: number; nom: string }[] = [];
      let current: number | null = leafId;
      let guard = 0;
      while (current && guard < 6) {
        guard += 1;
        const { data } = await supabase
          .from('categorieservices')
          .select('id, nom, parent_id')
          .eq('id', current)
          .maybeSingle();
        if (!data) break;
        chain.unshift({ id: Number(data.id), nom: String(data.nom || '') });
        current = data.parent_id ? Number(data.parent_id) : null;
      }
      if (!cancelled) setCategoryChain(chain.filter((c) => c.nom));
    })().catch(() => { if (!cancelled) setCategoryChain([]); });
    return () => { cancelled = true; };
  }, [service?.categorie_id]);

  // Résolution de l'organisation du socle prestataire (pour les avis, spec 54).
  useEffect(() => {
    const commerceId = service?.idcommerce ?? service?.owner ?? null;
    if (!commerceId) {
      setOrganizationId(null);
      return;
    }
    let cancelled = false;
    getOrganizationByCommerce(Number(commerceId))
      .then((o) => { if (!cancelled) setOrganizationId(o?.id ?? null); })
      .catch(() => { if (!cancelled) setOrganizationId(null); });
    return () => { cancelled = true; };
  }, [service?.idcommerce, service?.owner]);

  // Recherche d'un service de réservation lié (pour la réservation en ligne)
  useEffect(() => {
    if (!service?.owner) return;
    let cancelled = false;
    supabase
      .from('booking_services')
      .select('id, service_name')
      .eq('owner', service.owner)
      .eq('statut', 1)
      .then(({ data }) => {
        if (cancelled || !data || data.length === 0) return;
        const exact = data.find(
          (b) => b.service_name?.toLowerCase() === service.titre?.toLowerCase(),
        );
        setBookingServiceId((exact || data[0]).id);
      })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [service?.owner, service?.titre]);

  const images = service?.product_image
    ? service.product_image.split('|').filter(Boolean).filter((url) => {
        const lower = url.toLowerCase();
        return !(lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.endsWith('.mov'));
      })
    : [];

  const price = Number(service?.prix || 0);
  const promo = service?.prix_promo != null ? Number(service.prix_promo) : null;
  const detailText = htmlToText(service?.detailssup) || htmlToText(service?.description);

  const whatsappUrl = service
    ? buildWhatsAppUrl(
        whatsapp.number,
        `Bonjour ${tenant?.nomcommerce || ''}, je suis intéressé par votre service : ${service.titre} (${display(promo ?? price, monaie)}).`,
      )
    : '#';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4">
        <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
          <i className="ri-service-line text-2xl text-foreground-400"></i>
        </div>
        <h2 className="text-xl font-bold font-heading text-foreground-950 mb-2">Service introuvable</h2>
        <p className="text-sm text-foreground-500 mb-6">Ce service n&apos;existe pas ou n&apos;est plus disponible.</p>
        <Link to="/services" className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium no-underline hover:bg-primary-600 transition-colors whitespace-nowrap">
          Voir les services
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-6 md:py-10">
        {/* Fil d'ariane */}
        <nav className="flex items-center gap-1.5 text-xs text-foreground-400 mb-6 flex-wrap">
          <Link to="/" className="hover:text-foreground-700 no-underline whitespace-nowrap">Accueil</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/services" className="hover:text-foreground-700 no-underline whitespace-nowrap">Nos services</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-600 truncate max-w-[200px]">{service.titre}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Visuel */}
          <div>
            <div className="aspect-square bg-background-100 rounded-xl overflow-hidden border border-background-200/70">
              {images[activeImage] ? (
                <img src={images[activeImage]} alt={service.titre} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <i className="ri-service-line text-4xl text-foreground-300"></i>
                </div>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex items-center gap-3 mt-4 flex-wrap">
                {images.map((img, i) => (
                  <button
                    key={img}
                    type="button"
                    onClick={() => setActiveImage(i)}
                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 cursor-pointer transition-colors ${
                      activeImage === i ? 'border-primary-500' : 'border-background-200/70 hover:border-background-300/60'
                    }`}
                  >
                    <img src={img} alt={`${service.titre} ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Infos */}
          <div>
            {/* Chaîne de catégories complète (Catégorie › Sous-catégorie › Sous-sous) */}
            {categoryChain.length > 0 && (
              <nav className="flex items-center gap-1.5 flex-wrap mb-3" aria-label="Catégories du service">
                {categoryChain.map((c, idx) => (
                  <span key={c.id} className="flex items-center gap-1.5">
                    {idx > 0 && <i className="ri-arrow-right-s-line text-foreground-300 text-sm"></i>}
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-medium whitespace-nowrap">
                      <i className="ri-price-tag-3-line text-[10px]"></i>
                      {c.nom}
                    </span>
                  </span>
                ))}
              </nav>
            )}
            <div className="flex items-start justify-between gap-4 mb-3">
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                {service.titre}
              </h1>
              <FavoriteButton
                entityType="service"
                entityId={service.id}
                label={service.titre}
                metadata={{
                  image: images[0] || null,
                  ville: service.ville || null,
                  price: promo ?? price,
                  currency: monaie,
                }}
                className="mt-1 flex-shrink-0"
              />
            </div>

            {/* Prix */}
            <div className="flex items-end gap-3 flex-wrap mb-4">
              <span className="text-2xl md:text-3xl font-bold text-primary-600">
                {display(promo ?? price, monaie)}
              </span>
              {promo != null && (
                <span className="text-base text-foreground-400 line-through mb-1">
                  {display(price, monaie)}
                </span>
              )}
              {original(promo ?? price, monaie) && (
                <span className="text-xs text-foreground-400 mb-1">{original(promo ?? price, monaie)}</span>
              )}
            </div>

            {service.tags && (
              <div className="flex items-center gap-1.5 flex-wrap mb-5">
                {service.tags.split(',').map((tag) => (
                  <span key={tag} className="px-2.5 py-0.5 bg-secondary-50 text-secondary-700 rounded-full text-xs">
                    {tag.trim()}
                  </span>
                ))}
              </div>
            )}

            {service.description && (
              <p className="text-sm text-foreground-600 leading-relaxed mb-6 whitespace-pre-line">
                {service.description}
              </p>
            )}

            {detailText && detailText !== service.description && (
              <div className="mb-6 p-4 rounded-lg bg-background-100/70 border border-background-200/60">
                <p className="text-sm text-foreground-600 leading-relaxed whitespace-pre-line">{detailText}</p>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-3">
              {bookingServiceId ? (
                <Link
                  to={`/booking/${bookingServiceId}`}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-bold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap no-underline"
                >
                  <i className="ri-calendar-check-line"></i>
                  Réserver en ligne
                </Link>
              ) : (
                <div className="w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-medium bg-background-100 text-foreground-400 whitespace-nowrap">
                  <i className="ri-calendar-line"></i>
                  Réservation non disponible
                </div>
              )}

              {Number(service.form_id || 0) > 0 && (
                <Link
                  to={`/forms/${service.form_id}`}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-bold bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap no-underline"
                >
                  <i className="ri-survey-line"></i>
                  Remplir le formulaire
                </Link>
              )}

              {whatsapp.enabled && (
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-bold bg-green-500 text-white hover:bg-green-600 transition-colors cursor-pointer whitespace-nowrap no-underline"
                >
                  <i className="ri-whatsapp-line text-lg"></i>
                  Commander sur WhatsApp
                </a>
              )}
            </div>

            {(service.ville || service.pays) && (
              <div className="mt-6 pt-5 border-t border-background-200/70 flex items-center gap-2 text-sm text-foreground-500">
                <i className="ri-map-pin-line text-primary-500"></i>
                <span>{[service.ville, service.pays].filter(Boolean).join(', ')}</span>
              </div>
            )}
          </div>
        </div>

        <div className="mt-10">
          <ReviewsSection
            organizationId={organizationId}
            entityType="service"
            entityId={service.id}
            title="Avis sur ce service"
            emptyLabel="Aucun avis sur ce service pour le moment. Soyez le premier à donner votre avis."
          />
        </div>

        <RecommendedSection
          kind="service"
          excludeId={service.id}
          tenantId={service.idcommerce ?? (isTenant && tenant ? Number(tenant.id) : null)}
          currency={monaie}
          title="Vous aimerez aussi"
        />
      </div>
    </div>
  );
}